import { PATHS } from '@constants/paths';
import { Box, Button, Container, Typography } from '@mui/material';
import { Component, ErrorInfo, ReactNode } from 'react';

import { reportClientError } from '@shared/telemetry';
import { getEnv } from '@shared/utils';
import { ErrorFallbackFriendly } from './ErrorFallbackFriendly';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

const CHUNK_LOAD_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'Failed to load module script',
];

const isChunkLoadError = (error: Error): boolean => {
  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => error.message.includes(pattern));
};

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (this.isChunkLoadError(error)) {
      window.location.reload();
      return;
    }
    this.setState({ errorInfo });
    console.error('[ErrorBoundary]', error, errorInfo);
    void reportClientError(
      {
        source: 'boundary',
        kind: 'client',
        message: error.message,
        stack: error.stack,
        context: { componentStack: errorInfo.componentStack?.slice(0, 2000) },
      }
    );
  }

  private isChunkLoadError(error: Error): boolean {
    const message = error.message || '';
    return (
      message.includes('Failed to fetch dynamically imported module') ||
      message.includes('Loading chunk') ||
      message.includes('Loading CSS chunk')
    );
  }

  handleReload = (): void => {
    const currentError = this.state.error;
    const isNewBuildChunkError = currentError ? isChunkLoadError(currentError) : false;

    if (!isNewBuildChunkError) {
      localStorage.clear();
      sessionStorage.clear();
    }
    window.location.reload();
  };

  handleGoHome = (): void => {
    window.location.assign(PATHS.HOME);
  };

  renderDebugFallback(error: Error, errorInfo: ErrorInfo | null): ReactNode {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            mt: 4,
            p: 3,
            border: '1px solid',
            borderColor: 'error.main',
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}>
          <Typography variant="h6" color="error.dark" gutterBottom>
            Algo salió mal
          </Typography>
          <Typography variant="body2" component="pre" sx={{ overflow: 'auto', mb: 2, fontSize: '0.85rem' }}>
            {error.toString()}
          </Typography>
          {errorInfo?.componentStack && (
            <Typography variant="caption" component="pre" sx={{ overflow: 'auto', display: 'block', opacity: 0.9 }}>
              {errorInfo.componentStack}
            </Typography>
          )}
          <Typography variant="body2" sx={{ mt: 1 }}>
            Ocurrió un error inesperado. Puedes recargar para reintentar.
          </Typography>
          <Button variant="contained" onClick={this.handleReload} sx={{ mt: 2 }}>
            Recargar página
          </Button>
        </Box>
      </Container>
    );
  }

  renderChunkFallback(): ReactNode {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            mt: 4,
            p: 3,
            border: '1px solid',
            borderColor: 'info.main',
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}>
          <Typography variant="h6" color="info.dark" gutterBottom>
            Nuevo build detectado
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, fontSize: '0.95rem' }}>
            Detectamos que hay una nueva versión publicada. Recarga la página para continuar con la versión
            actualizada.
          </Typography>
          <Button variant="contained" onClick={this.handleReload} sx={{ mt: 2 }}>
            Actualizar ahora
          </Button>
        </Box>
      </Container>
    );
  }

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      const { error, errorInfo } = this.state;
      const isNewBuildChunkError = isChunkLoadError(error);

      if (isNewBuildChunkError) {
        return this.renderChunkFallback();
      }

      if (getEnv().VITE_DEBUG) {
        return this.renderDebugFallback(error, errorInfo);
      }

      return <ErrorFallbackFriendly onGoHome={this.handleGoHome} />;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
